import React, { useState, useCallback, useEffect, useMemo, useContext } from 'react';
import { StatusColourContext, StatusColourEntry } from '../contexts/StatusColourContext';
import { AuthContext } from '../contexts/AuthContext';
import MasterService, { StatusMasterType } from '../services/master.service';

interface StatusColourProviderProps {
  children: React.ReactNode;
}

type ColourMaps = Record<StatusMasterType, Map<number, StatusColourEntry>>;

const TYPES: StatusMasterType[] = ['tenantStatuses', 'subscriptionStatuses', 'paymentStatuses', 'featureFlagStatuses'];

const emptyMaps = (): ColourMaps => ({
  tenantStatuses: new Map(),
  subscriptionStatuses: new Map(),
  paymentStatuses: new Map(),
  featureFlagStatuses: new Map(),
});

const StatusColourProvider: React.FC<StatusColourProviderProps> = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [maps, setMaps] = useState<ColourMaps>(emptyMaps);

  const load = useCallback(async () => {
    const results = await Promise.all(
      TYPES.map((type) => MasterService.list({ type, includeDeleted: false, limit: 100 }).catch(() => null))
    );
    const next = emptyMaps();
    results.forEach((response: any, index) => {
      const type = TYPES[index];
      const items = response?.data?.items ?? [];
      items.forEach((item: any) => {
        next[type].set(item.id, { colour: item.colour_code, label: item.status_name });
      });
    });
    setMaps(next);
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const getColour = useCallback(
    (type: StatusMasterType, id?: number | null) => (id === null || id === undefined ? undefined : maps[type].get(id)),
    [maps]
  );

  const value = useMemo(() => ({ getColour, refresh: load }), [getColour, load]);

  return <StatusColourContext.Provider value={value}>{children}</StatusColourContext.Provider>;
};

export default StatusColourProvider;
